/**
 * Единственные часы репозитория и единственная арифметика времени.
 * 00-integration.md §Хребет данных 1.1–1.3.
 *
 * Три представления, и переходы между ними делают ТОЛЬКО функции этого файла:
 *   SQLite            — миллисекунды Unix UTC, целое, колонка *_at_ms
 *   подписываемый payload — целые unix-секунды (i64 big-endian)
 *   граница HTTP и UI — строка ISO-8601 UTC с миллисекундами и Z
 *
 * Ни одного `new Date(...)`, `Date.parse(...)`, `Date.now()`, `.toISOString()` вне этого файла —
 * правило R11 стража границ (tools/check-boundaries.ts).
 *
 * Temporal НЕ используется. На Node 26.4.0 его не было вовсе, на 26.7.0 он появился —
 * запрет остаётся в силе по существу, а не по доступности: вся арифметика идёт по целым
 * миллисекундам, а проекция в часовой пояс — через Intl.DateTimeFormat. Второй источник
 * времязависимой логики означал бы второй набор краевых случаев на переходах DST.
 * Запрет машинно проверяется правилом R11.
 */

export const ISO_UTC_MS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export class TimeError extends Error {
  override readonly name = 'TimeError';
  readonly code: TimeErrorCode;
  constructor(code: TimeErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

export type TimeErrorCode =
  | 'iso_format'
  | 'iso_parse'
  | 'not_safe_integer'
  | 'unknown_time_zone'
  | 'local_time_malformed'
  | 'local_time_does_not_exist';

/**
 * Единственные часы репозитория.
 * Тесты подменяют её через инъекцию (передачей nowMs как аргумента), а не глобальным моком.
 */
export function nowMs(): number {
  return Date.now();
}

export function msToUnixSeconds(ms: number): number {
  if (!Number.isSafeInteger(ms)) {
    throw new TimeError('not_safe_integer', `msToUnixSeconds: ${ms} is not a safe integer`);
  }
  return Math.floor(ms / 1000);
}

export function unixSecondsToMs(s: number): number {
  if (!Number.isSafeInteger(s)) {
    throw new TimeError('not_safe_integer', `unixSecondsToMs: ${s} is not a safe integer`);
  }
  return s * 1000;
}

/** Всегда с миллисекундами и суффиксом Z. Результат проверяется регуляркой перед возвратом. */
export function msToIso(ms: number): string {
  if (!Number.isSafeInteger(ms)) {
    throw new TimeError('not_safe_integer', `msToIso: ${ms} is not a safe integer`);
  }
  const iso = new Date(ms).toISOString();
  if (!ISO_UTC_MS.test(iso)) {
    throw new TimeError('iso_format', `msToIso produced a non-canonical string: ${iso}`);
  }
  return iso;
}

/**
 * Принимает ТОЛЬКО каноническую форму. Регулярка стоит ДО Date.parse, и это обязательно:
 * "2026-08-25T09:00:00" без Z молча трактуется как локальное время (на московской машине —
 * на три часа меньше правильного), а "2026-08-25T09:00:00Z" без миллисекунд разбирается,
 * но порождает вторую байтовую форму того же момента. Обе отвергаются.
 */
export function isoToMs(iso: string): number {
  if (!ISO_UTC_MS.test(iso)) {
    throw new TimeError(
      'iso_format',
      `isoToMs: expected YYYY-MM-DDTHH:MM:SS.sssZ, got ${JSON.stringify(iso)}`,
    );
  }
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    throw new TimeError('iso_parse', `isoToMs: unparsable instant ${JSON.stringify(iso)}`);
  }
  return ms;
}

// ---------------------------------------------------------------------------
// Даты внешних фидов. 05-seams.md Р-C7.1, 01-monitoring.md §6.1.4.
// ---------------------------------------------------------------------------

export type FeedDateFormat = 'rfc822' | 'iso8601' | 'unknown';

/**
 * Обе формы распознаются регулярками ДО Date.parse — голый Date.parse на произвольной
 * строке разбирает "22" как 2001 год. Год — только четырёхзначный: правило окна для
 * двузначных у каждой библиотеки своё. Зона — только GMT/UT/Z и числовое смещение:
 * «EST» без даты неоднозначен, а «MSK» вообще не из RFC 822.
 */
const RFC822_DATE =
  /^(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s*,\s*)?(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(GMT|UT|Z|[+-]\d{4})$/i;
/**
 * RFC 822 с ДВУЗНАЧНЫМ годом — единственное исключение из правила «год только
 * четырёхзначный» (решение 2026-11-02). Так пишет pubDate платформа IR-сайтов Equisolve:
 * «Tue, 27 Oct 26 09:20:00 -0400» у AMD, Intel, KLA, Dell и Marvell — замер 2026-11-02, все
 * 10–12 элементов каждой ленты приходили без даты. Это исходная запись самого RFC 822:
 * четыре цифры года ввёл RFC 1123.
 *
 * Принимается ТОЛЬКО полная форма: день недели, день, месяц словом, год, время, зона из
 * набора строгой ветки. Год читается как 20yy, и день недели обязан совпасть с получившейся
 * датой. Век сдвигает день недели на пять или шесть дней, поэтому 19yy, прочитанный как
 * 20yy, не проходит никогда. Без дня недели или без времени («14 Oct 26», «28.09.26»)
 * двузначный год по-прежнему отвергается.
 */
const RFC822_DATE_YY =
  /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s*,\s*(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(GMT|UT|Z|[+-]\d{4})$/i;
const FEED_ISO_DATE =
  /^(\d{4})-(\d{2})-(\d{2})[Tt ](\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(?:Z|z|[+-]\d{2}:?\d{2})$/;
/**
 * Метка зоны в формах ниже — закрытый словарь, решение 2026-11-02. ET, CT и PT — время
 * Нью-Йорка, Чикаго и Лос-Анджелеса без указания сезона, так печатает PR Newswire: смещение
 * на ЭТУ дату берётся из IANA-зоны, летнее или зимнее — решает tzdata. EST/EDT, CST/CDT и
 * PST/PDT сезон называют сами и берутся как написано, UTC и GMT — ноль. Любая другая метка —
 * отказ, а не догадка: «IST» — это Индия, Ирландия и Израиль сразу.
 *
 * Строгая ветка RFC 822 словаря НЕ получает: её зона по-прежнему только GMT/UT/Z и числовое
 * смещение, и «Fri, 22 Aug 2026 10:30:00 EST» отвергается, как раньше.
 */
const ZONE_LABEL_IANA: Readonly<Record<string, string>> = {
  ET: 'America/New_York',
  CT: 'America/Chicago',
  PT: 'America/Los_Angeles',
};
const ZONE_LABEL_OFFSET_MIN: Readonly<Record<string, number>> = {
  EST: -300,
  EDT: -240,
  CST: -360,
  CDT: -300,
  PST: -480,
  PDT: -420,
  UTC: 0,
  GMT: 0,
};
/**
 * Месяц словом, день, год, время и метка зоны: «Oct 27, 2026, 05:00 ET» — карточки PR
 * Newswire. Замер 2026-11-02: у Baidu, Kuaishou и Broadcom 25 из 25 карточек без даты.
 */
const FEED_NAMED_ZONED =
  /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})(?:\s*,\s*|\s+)(\d{4})\s*,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+([a-z]{2,4})$/i;
/**
 * День, ПОЛНОЕ имя месяца, год, время и метка зоны: «28 October 2026 11:00:00 EDT» — лента
 * AEA Research Highlights, все 835 элементов без даты (замер 2026-11-02). Полное имя месяца —
 * граница со строгой веткой RFC 822: «22 Aug 2026 10:30:00 EST» остаётся там и отвергается.
 * Пересечение одно — «May», у него короткое и полное имя совпадают, поэтому
 * «22 May 2026 10:30:00 EST» читается этой формой.
 */
const FEED_FULL_MONTH_ZONED =
  /^(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})\s*,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+([a-z]{2,4})$/i;
/**
 * Вывод Java `Date.toString()`: «Thu Oct 29 12:00:09 UTC 2026» — RSS пресс-релизов Oracle.
 * Метку зоны здесь печатает JVM из МИРОВОЙ таблицы коротких имён: у сервера в Шанхае «CST»
 * значит Китай, а не Чикаго. Поэтому из словаря берутся только UTC и GMT, прочее — отказ.
 */
const FEED_JAVA_DATE =
  /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})\s+(UTC|GMT)\s+(\d{4})$/i;
/**
 * Даты БЕЗ времени. Листинг блога времени почти никогда не печатает: AthenaHQ выводит
 * «Aug 31, 2026» простым текстом, и до 01.09.2026 такой материал получал
 * `publishedAtOrigin: 'fetched_at'`, а с ним — вечнозелёный формат навсегда и `422
 * no_date_for_dated_format` при попытке стать новостью.
 *
 * Час подставляется полночью UTC — не «сейчас»: подстановка текущего времени посадила бы
 * недатированный материал на вершину ранжирования по свежести. Полночь занижает, и это
 * ровно та сторона ошибки, которая безопасна.
 *
 * Пробел после запятой не обязателен: Intel Newsroom печатает «October 28,2026» (замер
 * 2026-11-02, 135 карточек без даты).
 */
const FEED_DATE_ONLY_NAMED =
  /^(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*\s*,?\s*)?(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})(?:\s*,\s*|\s+)(\d{4})$/i;
/**
 * Та же дата без времени, но день впереди: «Wed, 14 Oct 2026», «14 October 2026». Так
 * пишет pubDate фид McKinsey (`mckinsey.com/insights/rss`, замер 14.10.2026) — RFC 822 с
 * отрезанным временем и зоной. Строгая форма RFC822_DATE её не берёт, и все 50 элементов
 * фида приходили без даты.
 *
 * Запятая после месяца допускается, регистр месяца любой: «20 OCT, 2026» (блог Fireworks),
 * «29 Oct, 2026» (пресс-релизы Omdia) — замер 2026-11-02, обе ленты без единой даты.
 */
const FEED_DATE_ONLY_DAY_FIRST =
  /^(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*\s*,?\s*)?(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*(?:\s*,\s*|\s+)(\d{4})$/i;
const FEED_DATE_ONLY_NUMERIC = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Год впереди через точку или косую черту: «2026.10.27» (NAVER), «2026/10/26» (Hon Hai),
 * «2026/09/10» (DeepSeek). При годе впереди порядок один — год, месяц, день, и день с
 * месяцем не путаются, в отличие от «10/8/2026». Разделитель на всю дату один.
 */
const FEED_DATE_ONLY_YEAR_FIRST = /^(\d{4})([./])(\d{1,2})\2(\d{1,2})$/;
/**
 * Русские листинги (замер 14.10.2026). ВЦИОМ и Kept печатают «09 сентября 2026», ФОМ на
 * странице — «27 Ноября 2025»: месяц в родительном падеже, регистр любой, хвост «г.» или
 * «года» допускается. Месяц без дня («Октябрь 2026», «Яков и Партнёры») НЕ принимается:
 * подстановка первого числа старила бы материал на месяц, а не на часы.
 */
const FEED_DATE_ONLY_RU =
  /^(\d{1,2})\s+(января|февраля|марта|апреля|мая|июня|июля|августа|сентября|октября|ноября|декабря)\s+(\d{4})(?:\s*(?:г\.?|года))?$/i;
/**
 * «28.09.2026» — день впереди (Б1, замер 14.10.2026). Это русская и европейская запись;
 * американская пишет через косую черту, и «10/14/2026» здесь не принимается. Американская
 * дата с точками, попав сюда, либо не пройдёт календарную проверку («10.14.2026»), либо
 * прочтётся как день-месяц. Такая запись есть у Liquid AI в реестре AGI Boardroom
 * («10.15.2026», замер 2026-11-02), поэтому дата той строки берётся только из атрибута
 * `datetime`, а не из текста.
 */
const FEED_DATE_ONLY_DOTTED = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
/**
 * Короткая подпись перед датой: «Published: September 17, 2026» (витрина блога МВФ). Подпись —
 * одно-три слова из букв и двоеточие. Снимается один раз и только тогда, когда строка
 * целиком ни одной форме не подошла, поэтому всё, что разбиралось раньше, разбирается так же.
 */
const FEED_DATE_LABEL = /^\p{L}+(?:\s\p{L}+){0,2}\s*:\s*(\S.*)$/u;

const RU_MONTH_INDEX: Readonly<Record<string, number>> = {
  января: 1,
  февраля: 2,
  марта: 3,
  апреля: 4,
  мая: 5,
  июня: 6,
  июля: 7,
  августа: 8,
  сентября: 9,
  октября: 10,
  ноября: 11,
  декабря: 12,
};

const MONTH_INDEX: Readonly<Record<string, number>> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

/** Номер дня недели как у `getUTCDay()`: 0 — воскресенье. */
const WEEKDAY_INDEX: Readonly<Record<string, number>> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

/**
 * Date.parse молча перекатывает несуществующий день ("31 Feb" → 3 марта) — и в легаси-форме,
 * и в ISO с пробелом-разделителем. Календарная проверка по захваченным группам стоит до него.
 */
function isRealCalendarDay(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= (days[month - 1] as number);
}

type Groups4 = [string, string, string, string];

/**
 * Дата без времени → [год, месяц, день] по одной из форм выше; null — ни одна не подошла.
 * Календарную проверку делает вызывающий: здесь только распознавание формы.
 */
function dateOnlyOf(s: string): readonly [number, number, number] | null {
  const named = FEED_DATE_ONLY_NAMED.exec(s);
  if (named !== null) {
    const [, mon, day, year] = named as unknown as Groups4;
    return [Number(year), MONTH_INDEX[mon.slice(0, 3).toLowerCase()] as number, Number(day)];
  }
  const dayFirst = FEED_DATE_ONLY_DAY_FIRST.exec(s);
  if (dayFirst !== null) {
    const [, day, mon, year] = dayFirst as unknown as Groups4;
    return [Number(year), MONTH_INDEX[mon.slice(0, 3).toLowerCase()] as number, Number(day)];
  }
  const ru = FEED_DATE_ONLY_RU.exec(s);
  if (ru !== null) {
    const [, day, mon, year] = ru as unknown as Groups4;
    return [Number(year), RU_MONTH_INDEX[mon.toLowerCase()] as number, Number(day)];
  }
  const dotted = FEED_DATE_ONLY_DOTTED.exec(s);
  if (dotted !== null) {
    const [, day, month, year] = dotted as unknown as Groups4;
    return [Number(year), Number(month), Number(day)];
  }
  const yearFirst = FEED_DATE_ONLY_YEAR_FIRST.exec(s);
  if (yearFirst !== null) {
    const [, year, , month, day] = yearFirst as unknown as [string, string, string, string, string];
    return [Number(year), Number(month), Number(day)];
  }
  const numeric = FEED_DATE_ONLY_NUMERIC.exec(s);
  if (numeric !== null) {
    const [, year, month, day] = numeric as unknown as Groups4;
    return [Number(year), Number(month), Number(day)];
  }
  return null;
}

const FEED_DATE_MIN_MS = Date.UTC(2000, 0, 1);
const FEED_DATE_MAX_AHEAD_MS = 400 * 86_400_000;

/** Группы форм «дата, время, зона»; секунды необязательны. */
type ZonedGroups = [string, string, string, string, string, string, string | undefined, string];
type ClockGroups = { readonly hh: string; readonly mm: string; readonly ss: string | undefined };

/**
 * Группы RFC 822 → инстант; null — такого дня нет в календаре. 'UT' и 'Z' законны по
 * RFC 822, но V8 понимает их нестабильно между формами — приводим к 'GMT' и отдаём
 * Date.parse уже пересобранную строку без дня недели: день недели V8 игнорирует, а врущий
 * ("Mon" у пятницы) пусть не влияет на результат. Сверяет день недели только ветка
 * двузначного года: там он — доказательство века.
 */
function rfc822Ms(
  day: string,
  mon: string,
  year: number,
  time: ClockGroups,
  zone: string,
): number | null {
  const month = MONTH_INDEX[mon.toLowerCase()] as number;
  if (!isRealCalendarDay(year, month, Number(day))) return null;
  const z = zone.toUpperCase();
  const tz = z === 'UT' || z === 'Z' ? 'GMT' : z;
  return Date.parse(`${day} ${mon} ${year} ${time.hh}:${time.mm}:${time.ss ?? '00'} ${tz}`);
}

/** Распознанные группы «дата, время» → стенное время; месяц уже числом. */
function wallOf(year: string, month: number, day: string, time: ClockGroups): WallParts {
  return {
    year: Number(year),
    month,
    day: Number(day),
    hour: Number(time.hh),
    minute: Number(time.mm),
    second: Number(time.ss ?? '0'),
  };
}

/**
 * Стенное время с меткой зоны из словаря → инстант; null — метки нет в словаре, дня нет в
 * календаре или такого времени в зоне не бывает. Сезон ET/CT/PT считает только
 * wallMinuteToMs: время из весенней «дыры» не существует, и это отказ, а повторный осенний
 * час даёт РАННИЙ из двух инстантов. Дата выходит на час раньше истины, но не позже, и
 * курсор источника не уезжает вперёд реального времени.
 */
function zonedWallMs(w: WallParts, label: string): number | null {
  if (!isRealCalendarDay(w.year, w.month, w.day)) return null;
  if (w.hour > 23 || w.minute > 59 || w.second > 59) return null;
  const zone = label.toUpperCase();
  const offsetMin = ZONE_LABEL_OFFSET_MIN[zone];
  if (offsetMin !== undefined) {
    return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - offsetMin * 60_000;
  }
  const tz = ZONE_LABEL_IANA[zone];
  if (tz === undefined) return null;
  const p2 = (n: number): string => String(n).padStart(2, '0');
  const local = `${String(w.year).padStart(4, '0')}-${p2(w.month)}-${p2(w.day)}T${p2(w.hour)}:${p2(w.minute)}`;
  try {
    return wallMinuteToMs(local, tz).ms + w.second * 1000;
  } catch {
    return null;
  }
}

/**
 * Разбор НЕДОВЕРЕННОЙ даты из внешнего источника. Отдельная функция, а не расширение isoToMs:
 * isoToMs заверяет наш собственный канон и обязана оставаться строгой.
 *
 * Возвращает null на всём, что не разобралось. Не бросает: битая дата в чужом фиде — это
 * рядовое событие, а не ошибка программы. Зажим min(parsed, fetched_at) и отказ на «дате
 * в будущем» живут НЕ здесь, а в конвейере (sources.md ПРАВИЛО 6.9): разбор не знает
 * fetched_at, и склеивать их значило бы потерять признак «дату разобрать не удалось».
 *
 * format: 'rfc822' и 'iso8601' — строгие ветки (RFC 822 — и с двузначным годом), 'unknown' —
 * всё остальное: даты без времени, формы с меткой зоны и вывод Java.
 *
 * По-прежнему отвергается, и тесты это держат: дата с неизвестным порядком дня и месяца
 * («10/8/2026», «10.15.2026», «30.10.26»), ISO-время без зоны (неверная зона увела бы курсор
 * источника вперёд реального времени и спрятала бы следующие материалы), относительные даты
 * («4 days ago») и даты без года.
 */
export function parseFeedDate(raw: string): { ms: number; format: FeedDateFormat } | null {
  // Нормализация: BOM, обрезка, схлопывание пробелов — фиды переносят даты и табами.
  const s = raw
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/\s+/g, ' ');
  if (s === '') return null;
  const whole = feedDateOf(s);
  if (whole !== null) return whole;
  const labelled = FEED_DATE_LABEL.exec(s);
  return labelled === null ? null : feedDateOf(labelled[1] as string);
}

/** Нормализованная строка → инстант по первой подошедшей форме; null — ни одной. */
function feedDateOf(s: string): { ms: number; format: FeedDateFormat } | null {
  let ms: number | null;
  let format: FeedDateFormat;

  const rfc = RFC822_DATE.exec(s);
  if (rfc !== null) {
    const [, day, mon, year, hh, mm, ss, zone] = rfc as unknown as ZonedGroups;
    ms = rfc822Ms(day, mon, Number(year), { hh, mm, ss }, zone);
    format = 'rfc822';
  } else if (RFC822_DATE_YY.test(s)) {
    const [, weekday, day, mon, yy, hh, mm, ss, zone] = RFC822_DATE_YY.exec(s) as unknown as [
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string | undefined,
      string,
    ];
    const year = 2000 + Number(yy);
    const month = MONTH_INDEX[mon.toLowerCase()] as number;
    if (!isRealCalendarDay(year, month, Number(day))) return null;
    const actual = new Date(Date.UTC(year, month - 1, Number(day))).getUTCDay();
    if (WEEKDAY_INDEX[weekday.toLowerCase()] !== actual) return null;
    ms = rfc822Ms(day, mon, year, { hh, mm, ss }, zone);
    format = 'rfc822';
  } else if (FEED_ISO_DATE.test(s)) {
    const [, year, month, day] = FEED_ISO_DATE.exec(s) as unknown as [
      string,
      string,
      string,
      string,
    ];
    if (!isRealCalendarDay(Number(year), Number(month), Number(day))) return null;
    ms = Date.parse(s);
    format = 'iso8601';
  } else if (FEED_NAMED_ZONED.test(s)) {
    const [, mon, day, year, hh, mm, ss, zone] = FEED_NAMED_ZONED.exec(s) as unknown as ZonedGroups;
    const month = MONTH_INDEX[mon.slice(0, 3).toLowerCase()] as number;
    ms = zonedWallMs(wallOf(year, month, day, { hh, mm, ss }), zone);
    format = 'unknown';
  } else if (FEED_FULL_MONTH_ZONED.test(s)) {
    const [, day, mon, year, hh, mm, ss, zone] = FEED_FULL_MONTH_ZONED.exec(
      s,
    ) as unknown as ZonedGroups;
    const month = MONTH_INDEX[mon.slice(0, 3).toLowerCase()] as number;
    ms = zonedWallMs(wallOf(year, month, day, { hh, mm, ss }), zone);
    format = 'unknown';
  } else if (FEED_JAVA_DATE.test(s)) {
    const [, mon, day, hh, mm, ss, zone, year] = FEED_JAVA_DATE.exec(s) as unknown as [
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
    ];
    const month = MONTH_INDEX[mon.toLowerCase()] as number;
    ms = zonedWallMs(wallOf(year, month, day, { hh, mm, ss }), zone);
    format = 'unknown';
  } else {
    const ymd = dateOnlyOf(s);
    if (ymd === null) return null;
    const [year, month, day] = ymd;
    if (!isRealCalendarDay(year, month, day)) return null;
    ms = Date.UTC(year, month - 1, day);
    format = 'unknown';
  }

  if (ms === null || Number.isNaN(ms) || !Number.isSafeInteger(ms)) return null;
  if (ms < FEED_DATE_MIN_MS || ms > nowMs() + FEED_DATE_MAX_AHEAD_MS) return null;
  return { ms, format };
}

// ---------------------------------------------------------------------------
// Часовые пояса. Вся арифметика — здесь, три функции, больше нигде.
// ---------------------------------------------------------------------------

const PARTS_CACHE = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  const cached = PARTS_CACHE.get(tz);
  if (cached !== undefined) return cached;
  let fmt: Intl.DateTimeFormat;
  try {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    throw new TimeError('unknown_time_zone', `unknown IANA time zone: ${JSON.stringify(tz)}`);
  }
  PARTS_CACHE.set(tz, fmt);
  return fmt;
}

interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallParts(tz: string, instantMs: number): WallParts {
  const parts = partsFormatter(tz).formatToParts(new Date(instantMs));
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    for (const p of parts) {
      if (p.type === type) return Number(p.value);
    }
    throw new TimeError('unknown_time_zone', `formatToParts gave no ${type} for ${tz}`);
  };
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** Смещение зоны в миллисекундах в конкретный инстант. */
export function tzOffsetMs(tz: string, instantMs: number): number {
  const p = wallParts(tz, instantMs);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, 0);
  // instantMs может нести миллисекунды, которых нет в p — срезаем их с обеих сторон.
  return asUtc - Math.floor(instantMs / 1000) * 1000;
}

const PAD2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/** Инстант → «стенное» время зоны, минутная точность, форма 'YYYY-MM-DDTHH:MM'. */
export function msToWallMinute(ms: number, tz: string): string {
  if (!Number.isSafeInteger(ms)) {
    throw new TimeError('not_safe_integer', `msToWallMinute: ${ms} is not a safe integer`);
  }
  const p = wallParts(tz, ms);
  return `${String(p.year).padStart(4, '0')}-${PAD2(p.month)}-${PAD2(p.day)}T${PAD2(p.hour)}:${PAD2(p.minute)}`;
}

const LOCAL_MINUTE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * «Стенное» время зоны → инстант. Единственный способ материализовать слот.
 *
 * Две пробы и отбор по обратной проверке: арифметика по одному смещению ошибается на
 * переходах DST. Пусто → время не существует (весенний перевод). Два → зимний перевод,
 * берём более ранний инстант и помечаем ambiguous.
 */
export function wallMinuteToMs(local: string, tz: string): { ms: number; ambiguous: boolean } {
  const m = LOCAL_MINUTE.exec(local);
  if (m === null) {
    throw new TimeError(
      'local_time_malformed',
      `expected YYYY-MM-DDTHH:MM, got ${JSON.stringify(local)}`,
    );
  }
  const [, y, mo, d, h, mi] = m as unknown as [string, string, string, string, string, string];
  const naive = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), 0, 0);

  const DAY = 86_400_000;
  const candidates = [naive - tzOffsetMs(tz, naive - DAY), naive - tzOffsetMs(tz, naive + DAY)];
  const unique: number[] = [];
  for (const c of candidates) {
    if (!unique.includes(c)) unique.push(c);
  }

  const valid = unique.filter((ms) => msToWallMinute(ms, tz) === local).sort((a, b) => a - b);

  const first = valid[0];
  if (first === undefined) {
    throw new TimeError(
      'local_time_does_not_exist',
      `${local} does not exist in ${tz} (spring-forward gap)`,
    );
  }
  return { ms: first, ambiguous: valid.length > 1 };
}

/** 'YYYY-MM-DD' в зоне бренда. Публикатор считает свой day_key только так (§Ключ 5.2). */
export function dayKeyOf(ms: number, tz: string): string {
  return msToWallMinute(ms, tz).slice(0, 10);
}

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Полночь UTC календарной даты `YYYY-MM-DD` — только для календарной арифметики над датой. */
function dayKeyUtcMs(dayKey: string): number {
  const m = DAY_KEY.exec(dayKey);
  if (m === null) {
    throw new TimeError(
      'local_time_malformed',
      `expected YYYY-MM-DD, got ${JSON.stringify(dayKey)}`,
    );
  }
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * `YYYY-MM-DD`, который на самом деле существует в календаре (не 2026-02-30 и не 2026-13-01).
 * Живёт здесь, а не у вызывающего, потому что проверка идёт через календарную арифметику над
 * `Date.UTC`, а она разрешена только в этом модуле (правило R11).
 */
export function isDayKey(s: string): boolean {
  const m = DAY_KEY.exec(s);
  if (m === null) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d;
}

/**
 * ISO-день недели местной даты (`dayKeyOf`): 1 — понедельник … 7 — воскресенье, как
 * `weekday` слота. Дата уже местная, поэтому зона здесь не нужна: день недели у
 * календарной даты один в любой зоне.
 */
export function isoWeekdayOfDayKey(dayKey: string): number {
  const js = new Date(dayKeyUtcMs(dayKey)).getUTCDay();
  return js === 0 ? 7 : js;
}

/**
 * ISO-неделя местной даты в форме `YYYY-Www` (ISO 8601: неделя с понедельника, первая
 * неделя года — та, где четверг). Год — ISO-год недели, а не календарный: 29.12.2025 —
 * `2026-W01`, 01.01.2027 — `2026-W53`.
 */
export function isoWeekKeyOfDayKey(dayKey: string): string {
  const DAY = 86_400_000;
  // Четверг той же недели определяет ISO-год.
  const thursday = dayKeyUtcMs(dayKey) + (4 - isoWeekdayOfDayKey(dayKey)) * DAY;
  const isoYear = new Date(thursday).getUTCFullYear();
  const week = Math.floor((thursday - Date.UTC(isoYear, 0, 1)) / DAY / 7) + 1;
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}
